'use client';

import Link from 'next/link';
import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import { Box, Typography, Button, Container, Grid, Chip } from '@mui/material';
import { 
  LightbulbOutlined, 
  RocketLaunchOutlined, 
  SettingsInputComponentOutlined, 
  SecurityOutlined, 
  GroupsOutlined, 
  StarOutline,
  SupportOutlined
} from '@/lib/mui-optimized-imports';

const AboutPage = () => {
  return (
    <PublicPageLayout>
      <Box sx={{ py: 8, textAlign: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} mb={3} color="text.primary">
            Empowering Creators to Thrive in the Digital World
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={5}>
            At FloAI Studio, our mission is to revolutionize social media management, empowering creators, businesses, and agencies to streamline content creation, publishing, analysis, and monetization across all platforms with cutting-edge AI capabilities.
          </Typography>
          <Button
            component={Link}
            href="/auth/signup"
            variant="contained"
            color="primary"
            size="large"
            sx={(theme) => ({
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`,
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            })}
          >
            Start Your Free Trial
          </Button>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} mb={6} textAlign="center" color="text.primary">
            Unlock Your Creative Potential with FloAI Studio
          </Typography>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', width: '100%' }}>
                <LightbulbOutlined color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">AI-Powered Content Creation</Typography>
                <Typography variant="body1" color="text.secondary">
                  Leverage advanced AI to generate captivating captions, trending hashtags, stunning images, and innovative ideas, transforming your content strategy.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', width: '100%' }}>
                <RocketLaunchOutlined color="secondary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Smart Scheduling & Publishing</Typography>
                <Typography variant="body1" color="text.secondary">
                  Plan your content calendars, schedule posts across all major platforms, and optimize publishing times for maximum reach and engagement.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', width: '100%' }}>
                <SettingsInputComponentOutlined color="success" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Advanced Analytics & Insights</Typography>
                <Typography variant="body1" color="text.secondary">
                  Gain deep insights into your content performance, track engagement, analyze platform breakdowns, and understand your audience like never before.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ mt: 4 }}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', width: '100%' }}>
                <GroupsOutlined color="info" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Seamless Collaboration</Typography>
                <Typography variant="body1" color="text.secondary">
                  Work effortlessly with your team members, assign roles, manage permissions, and streamline content approval workflows for efficient operations.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} mb={4} color="text.primary">
            The FloAI Journey: Built for Innovation, Designed for You
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            FloAI Studio was born from a vision to simplify the complex world of digital content creation. Our journey has been defined by a relentless pursuit of technical excellence, ensuring our platform is not only powerful but also accessible, high-performing, and perfectly responsive across all devices. We&apos;ve undergone extensive development, delivering a robust suite of features and supporting numerous social media platforms to empower creators globally.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Chip label="WCAG 2.1 AA Compliant" color="primary" variant="outlined" />
            </Grid>
            <Grid item>
              <Chip label="Optimized for Core Web Vitals" color="secondary" variant="outlined" />
            </Grid>
            <Grid item>
              <Chip label="16+ Social Platforms Supported" color="info" variant="outlined" />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} mb={6} textAlign="center" color="text.primary">
            Why FloAI Stands Apart
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <StarOutline color="warning" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Comprehensive AI Suite</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Unlike generic social media tools, FloAI offers a deep integration of AI across content generation, optimization, analytics, and workflow automation, providing a truly intelligent platform.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <SecurityOutlined color="error" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Enterprise-Grade Solutions</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  From white-label options and custom integrations to advanced security and dedicated support, FloAI is built to scale with the needs of large businesses and agencies.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: 4 }}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <GroupsOutlined color="info" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Intuitive UI/UX</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Experience a clean, minimal, and highly professional user interface designed for efficiency and ease of use, making complex tasks simple.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: 4 }}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default', height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
                <SupportOutlined color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1} color="text.primary">Dedicated Community & Support</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Join a thriving community of creators and access a robust support system, including comprehensive FAQs, tutorials, and personalized assistance.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} mb={4} color="text.primary">
            Meet Our Visionaries
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 3, width: '100%' }}>
                <Typography variant="h5" fontWeight={700} mb={1} color="secondary.main">Lloyd Alexander</Typography>
                <Typography variant="subtitle1" color="text.secondary" mb={2}>Lead Developer (AI)</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Lloyd Alexander is the artificial intelligence behind FloAI, architecting the intelligent systems and features that make content creation seamless and effective.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 3, width: '100%' }}>
                <Typography variant="h5" fontWeight={700} mb={1} color="info.main">Renee</Typography>
                <Typography variant="subtitle1" color="text.secondary" mb={2}>Lead Marketing Specialist</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Renee is our marketing maestro, crafting strategies and campaigns that connect FloAI Studio with creators worldwide and amplify our message.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 8, textAlign: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} mb={3} color="text.primary">
            Ready to Elevate Your Content Strategy?
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={5}>
            Join FloAI Studio today and transform the way you create, manage, and grow your digital presence.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                component={Link}
                href="/auth/signup"
                variant="contained"
                color="primary"
                size="large"
                sx={(theme) => ({
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`,
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                })}
              >
                Start Your Free Trial
              </Button>
            </Grid>
            <Grid item>
              <Button
                component={Link}
                href="/features"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                Explore Features
              </Button>
            </Grid>
            <Grid item>
              <Button
                component={Link}
                href="/pricing"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                View Pricing
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </PublicPageLayout>
  );
};

export default AboutPage;
